import { DatePipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogContent, MatDialogTitle } from '@angular/material/dialog'
import { MatButton } from '@angular/material/button'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { addDays } from 'date-fns'
import { DatetimePickerComponent } from '../../common/datetime-picker/datetime-picker.component'
import { DATE_TIME_FORMAT, resolveLocalTimeZone } from '../../utils'

const DEFAULT_EXTENSION_IN_DAYS = 30

export type ExtendRetentionDialogData = {
  callName: string
  currentDeletionDate: Date
  /** The furthest the date may be pushed, or null when the organization sets no cap. */
  maximumDeletionDate: Date | null
}

export type ExtendRetentionDialogResult = {
  deletionDate: Date
}

@Component({
  selector: 'app-extend-retention-dialog',
  standalone: true,
  imports: [
    FormsModule,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatDialogTitle,
    MatButton,
    TranslatePipe,
    TranslateDirective,
    DatetimePickerComponent,
    DatePipe,
  ],
  templateUrl: './extend-retention-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './extend-retention-dialog.component.scss',
})
export class ExtendRetentionDialogComponent {
  data: ExtendRetentionDialogData = inject<ExtendRetentionDialogData>(MAT_DIALOG_DATA)

  // The picker reads and writes a plain Date, so what the user types is in their own time zone.
  // Both dates are shown in it, and it is named, rather than being quietly converted to London's.
  localTimeZone = resolveLocalTimeZone()
  dateTimeFormat = DATE_TIME_FORMAT

  currentDeletionDate = new Date(this.data.currentDeletionDate)
  maximumDeletionDate: Date | null = this.data.maximumDeletionDate === null ? null : new Date(this.data.maximumDeletionDate)

  // The default nudge is capped too, so the dialog never opens on a date it would reject.
  deletionDate: Date = this.withinMaximum(addDays(new Date(this.data.currentDeletionDate), DEFAULT_EXTENSION_IN_DAYS))

  private withinMaximum(date: Date): Date {
    return this.maximumDeletionDate !== null && date > this.maximumDeletionDate ? this.maximumDeletionDate : date
  }

  // Only pushing the date out is on offer here: pulling it in would destroy the recording earlier
  // than the organization agreed to, which is a support action rather than an administrator's.
  get isDeletionDateValid(): boolean {
    return this.deletionDate > this.currentDeletionDate && !this.isBeyondMaximum
  }

  get isBeyondMaximum(): boolean {
    return this.maximumDeletionDate !== null && this.deletionDate > this.maximumDeletionDate
  }

  get errorMessageKey(): string {
    return this.isBeyondMaximum ? 'CALLS.EXTEND_RETENTION.BEYOND_MAXIMUM' : 'CALLS.EXTEND_RETENTION.MUST_BE_LATER'
  }

  get dialogResult(): ExtendRetentionDialogResult {
    return { deletionDate: this.deletionDate }
  }
}
